import { ChevronDown, Plus, Settings, Sparkles, X } from "lucide-react";
import { useState } from "react";
import type { FormSchema } from "@/lib/database";
import { findFieldStepIndex, makeAiGroupId } from "@/lib/fields/ai-group";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
	createFieldFromType,
	FIELD_CATEGORIES,
	FIELD_TYPE_CONFIGS,
} from "@/lib/fields/field-config";
import {
	findEnabledRecognitionFieldInGroup,
	findEnabledRecognitionFieldOutsideGroups,
} from "@/lib/fields/recognition";
import { FieldSpecificSettings } from "../field-specific-settings";
import type { FieldSettingsProps } from "./types";

export function FieldGroupSettings({
	field,
	onUpdateSettings,
	onFieldUpdate,
	schema,
}: FieldSettingsProps) {
	const groupFields = field.settings?.groupFields || [];
	const groupLayout = field.settings?.groupLayout || "horizontal";
	const groupSpacing = field.settings?.groupSpacing || "normal";
	const groupColumns = field.settings?.groupColumns || 2;
	const [pickerOpen, setPickerOpen] = useState(false);

	// Динамическая группа полей с ИИ (t_b8ffa1fc): доступна только на шаге 2+
	const aiEnabled = field.settings?.aiEnabled === true;
	const aiPrompt = field.settings?.aiPrompt || "";
	const onStepTwoPlus = schema ? findFieldStepIndex(schema, field.id) >= 1 : true;

	const [expandedFields, setExpandedFields] = useState<{
		[id: string]: boolean;
	}>({});
	const t = useTranslations("fields");

	const addFieldToGroup = (fieldType: string) => {
		const newField = createFieldFromType(fieldType as any, t);
		// Распознавание: в группе — максимум у одного поля, а включённое поле ВНЕ групп
		// исключает групповые; у добавляемого поля оно выключено (t_aba782d3)
		if (
			findEnabledRecognitionFieldInGroup(field) ||
			findEnabledRecognitionFieldOutsideGroups(schema)
		) {
			newField.settings = {
				...newField.settings,
				recognition: {
					customPrompt: newField.settings?.recognition?.customPrompt,
					enabled: false,
					requireValidation:
						newField.settings?.recognition?.requireValidation ?? true,
				},
			};
		}
		const updatedGroupFields = [...groupFields, newField];
		onUpdateSettings({
			groupFields: updatedGroupFields,
		});
	};

	const removeFieldFromGroup = (fieldId: string) => {
		const updatedGroupFields = groupFields.filter((f) => f.id !== fieldId);
		onUpdateSettings({
			groupFields: updatedGroupFields,
		});
		setExpandedFields((prev) => {
			const copy = { ...prev };
			delete copy[fieldId];
			return copy;
		});
	};

	const updateGroupField = (fieldId: string, updates: any) => {
		const updatedGroupFields = groupFields.map((f) =>
			f.id === fieldId ? { ...f, ...updates } : f
		);
		onUpdateSettings({
			groupFields: updatedGroupFields,
		});
	};

	const setFieldExpansion = (fieldId: string, open: boolean) => {
		setExpandedFields((prev) => ({
			...prev,
			[fieldId]: open,
		}));
	};

	const handleSettingsButton = (fieldId: string) => {
		setFieldExpansion(fieldId, !expandedFields[fieldId]);
	};

	return (
		<div className="flex flex-col gap-4">
			<Card className="gap-2 p-4 shadow-none">
				<CardHeader className="p-0">
					<CardTitle className="flex items-center gap-2 text-lg">
						{t("group.layoutSettings")}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-2 p-0">
					<div className="flex flex-col gap-2">
						<Label className="font-medium text-sm" htmlFor="group-layout">
							{t("group.layoutDirection")}
						</Label>
						<Select
							onValueChange={(value) =>
								onUpdateSettings({
									groupLayout: value as "horizontal" | "vertical",
								})
							}
							value={groupLayout}
						>
							<SelectTrigger className="w-full" id="group-layout">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="horizontal">
									{t("group.horizontalSideBySide")}
								</SelectItem>
								<SelectItem value="vertical">{t("group.verticalStacked")}</SelectItem>
							</SelectContent>
						</Select>
						<p className="text-muted-foreground text-xs" id="group-layout-help">
							{t("group.howFieldsArranged")}
						</p>
					</div>

					{groupLayout === "horizontal" && (
						<div className="flex flex-col gap-2">
							<Label className="font-medium text-sm" htmlFor="group-columns">
								{t("group.numberOfColumns")}
							</Label>
							<Select
								onValueChange={(value) =>
									onUpdateSettings({
										groupColumns: Number.parseInt(value) as 2 | 3 | 4,
									})
								}
								value={groupColumns.toString()}
							>
								<SelectTrigger className="w-full" id="group-columns">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="2">{t("group.nColumns", { n: 2 })}</SelectItem>
									<SelectItem value="3">{t("group.nColumns", { n: 3 })}</SelectItem>
									<SelectItem value="4">{t("group.nColumns", { n: 4 })}</SelectItem>
								</SelectContent>
							</Select>
							<p
								className="text-muted-foreground text-xs"
								id="group-columns-help"
							>
								{t("group.columnsHelp")}
							</p>
						</div>
					)}

					<div className="flex flex-col gap-2">
						<Label className="font-medium text-sm" htmlFor="group-spacing">
							{t("group.fieldSpacing")}
						</Label>
						<Select
							onValueChange={(value) =>
								onUpdateSettings({
									groupSpacing: value as "compact" | "normal" | "relaxed",
								})
							}
							value={groupSpacing}
						>
							<SelectTrigger className="w-full" id="group-spacing">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="compact">{t("group.compact")}</SelectItem>
								<SelectItem value="normal">{t("group.normal")}</SelectItem>
								<SelectItem value="relaxed">{t("group.relaxed")}</SelectItem>
							</SelectContent>
						</Select>
						<p
							className="text-muted-foreground text-xs"
							id="group-spacing-help"
						>
							{t("group.spacingHelp")}
						</p>
					</div>
				</CardContent>
			</Card>

			<Card className="gap-2 p-4 shadow-none">
				<CardHeader className="p-0">
					<CardTitle className="flex items-center gap-2 text-lg">
						<Sparkles aria-hidden="true" className="size-4" />
						{t("group.ai.title")}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-2 p-0">
					<div className="flex items-center justify-between">
						<div className="flex flex-col gap-1">
							<Label
								className="font-medium text-sm"
								htmlFor={`${field.id}-ai-enabled`}
							>
								{t("group.ai.toggle")}
							</Label>
							<p
								className="text-muted-foreground text-xs"
								id={`${field.id}-ai-enabled-help`}
							>
								{t("group.ai.help")}
							</p>
							{!onStepTwoPlus && (
								<p className="text-amber-600 text-xs dark:text-amber-400">
									{t("group.ai.stepOneWarning")}
								</p>
							)}
						</div>
						<Switch
							aria-describedby={`${field.id}-ai-enabled-help`}
							checked={aiEnabled}
							disabled={!onStepTwoPlus}
							id={`${field.id}-ai-enabled`}
							name={`${field.id}-ai-enabled`}
							onCheckedChange={(checked) => {
								if (checked && !field.settings?.aiGroupId) {
									onUpdateSettings({
										aiEnabled: checked,
										aiGroupId: makeAiGroupId(),
									});
									return;
								}
								onUpdateSettings({ aiEnabled: checked });
							}}
						/>
					</div>

					{aiEnabled && (
						<div className="flex flex-col gap-2">
							<Label
								className="font-medium text-sm"
								htmlFor={`${field.id}-ai-prompt`}
							>
								{t("group.ai.promptLabel")}
							</Label>
							<textarea
								aria-describedby={`${field.id}-ai-prompt-help`}
								className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
								id={`${field.id}-ai-prompt`}
								maxLength={500}
								name={`${field.id}-ai-prompt`}
								onChange={(e) =>
									onUpdateSettings({ aiPrompt: e.target.value })
								}
								placeholder={t("group.ai.promptPlaceholder")}
								value={aiPrompt}
							/>
							<p
								className="text-muted-foreground text-xs"
								id={`${field.id}-ai-prompt-help`}
							>
								{t("group.ai.promptHelp")}
							</p>
							<div className="flex flex-col gap-1 rounded-md bg-muted/50 p-2">
								<span className="text-muted-foreground text-xs">
									{t("group.ai.groupIdLabel")}
								</span>
								<code className="font-mono text-xs">
									{field.settings?.aiGroupId || "—"}
								</code>
							</div>
						</div>
					)}
				</CardContent>
			</Card>

			<Card className="gap-2 p-4 shadow-none">
				<CardHeader className="p-0">
					<CardTitle className="flex items-center gap-2 text-lg">
						{t("group.groupFields", { count: groupFields.length })}
					</CardTitle>
				</CardHeader>
				<CardContent className="flex flex-col gap-2 p-0">
					<div className="flex justify-start">
						<Dialog onOpenChange={setPickerOpen} open={pickerOpen}>
							<DialogTrigger asChild>
								<Button
									aria-label={t("group.addFieldToGroup")}
									className="focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
									size="sm"
									type="button"
									variant="outline"
								>
									<Plus aria-hidden="true" className="size-4" />
									{t("group.addField")}
								</Button>
							</DialogTrigger>
							<DialogContent className="p-0">
								<DialogHeader className="px-4 pt-4">
									<DialogTitle>{t("group.addAField")}</DialogTitle>
								</DialogHeader>
								<ScrollArea className="max-h-[70dvh] p-4 pt-2">
									<div className="grid grid-cols-1 gap-6">
										{Object.entries(FIELD_CATEGORIES).map(([key, title]) => (
											<div className="flex flex-col gap-3" key={key}>
												<div className="px-1 text-muted-foreground text-xs uppercase tracking-wide">
													{t(`categories.${key}`)}
												</div>
												<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
													{FIELD_TYPE_CONFIGS.filter(
														(f) => f.category === (key as any)
													).map((f) => (
														<Button
															className="h-19 w-full items-center justify-start text-left"
															key={f.type}
															onClick={() => {
																addFieldToGroup(f.type);
																setPickerOpen(false);
															}}
															type="button"
															variant="outline"
														>
															<div className="flex flex-col">
																<span className="font-medium text-foreground text-sm">
																	{t(`types.${f.type}.label`)}
																</span>
																<span className="text-wrap text-muted-foreground text-xs">
																	{t(`types.${f.type}.description`)}
																</span>
															</div>
														</Button>
													))}
												</div>
											</div>
										))}
									</div>
								</ScrollArea>
							</DialogContent>
						</Dialog>
					</div>

					{groupFields.length === 0 ? (
						<div
							aria-live="polite"
							className="rounded-md border border-border border-dashed p-6 text-center text-muted-foreground"
							role="status"
						>
							<p className="text-sm">{t("group.noFieldsInGroup")}</p>
							<p className="text-xs">
								{t("group.addFieldsUsingDropdown")}
							</p>
						</div>
					) : (
						<div className="flex flex-col gap-3">
							{groupFields.map((groupField) => (
								<Card className="gap-2 p-4 shadow-none" key={groupField.id}>
									<div className="flex flex-col gap-4">
										<div className="flex items-center justify-between">
											<div className="flex items-center gap-2">
												<h4 className="font-medium text-card-foreground text-sm">
													{groupField.label || t(`types.${groupField.type}.label`)}
												</h4>
												<span className="rounded bg-muted px-2 py-1 text-muted-foreground text-xs">
													{groupField.type}
												</span>
											</div>
											<div className="flex items-center gap-1">
												<Button
													aria-label={t("group.settingsForField", { name: groupField.label || t(`types.${groupField.type}.label`) })}
													className="size-8"
													onClick={() => handleSettingsButton(groupField.id)}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															handleSettingsButton(groupField.id);
														}
													}}
													size="icon"
													type="button"
													variant="ghost"
												>
													<Settings aria-hidden="true" className="size-4" />
												</Button>
												<Button
													aria-label={t("group.removeField", { name: groupField.label || t(`types.${groupField.type}.label`) })}
													className="size-8"
													onClick={() => removeFieldFromGroup(groupField.id)}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															removeFieldFromGroup(groupField.id);
														}
													}}
													size="icon"
													type="button"
													variant="destructive"
												>
													<X aria-hidden="true" className="size-4" />
												</Button>
											</div>
										</div>

										<div className="grid grid-cols-1 gap-2 md:grid-cols-2">
											<div className="flex flex-col gap-2">
												<Label
													className="font-medium text-sm"
													htmlFor={`${groupField.id}-label`}
												>
													{t("group.fieldLabel")}
												</Label>
												<Input
													aria-describedby={`${groupField.id}-label-help`}
													autoComplete="off"
													id={`${groupField.id}-label`}
													name={`${groupField.id}-label`}
													onChange={(e) =>
														updateGroupField(groupField.id, {
															label: e.target.value,
														})
													}
													onKeyDown={(e) => {
														if (e.key === "Escape") {
															e.currentTarget.blur();
														}
													}}
													placeholder={t("group.enterFieldLabel")}
													type="text"
													value={groupField.label}
												/>
												<p
													className="text-muted-foreground text-xs"
													id={`${groupField.id}-label-help`}
												>
													{t("group.labelForThisField")}
												</p>
											</div>
											<div className="flex flex-col gap-2">
												<Label
													className="font-medium text-sm"
													htmlFor={`${groupField.id}-placeholder`}
												>
													{t("group.placeholderText")}
												</Label>
												<Input
													aria-describedby={`${groupField.id}-placeholder-help`}
													autoComplete="off"
													id={`${groupField.id}-placeholder`}
													name={`${groupField.id}-placeholder`}
													onChange={(e) =>
														updateGroupField(groupField.id, {
															placeholder: e.target.value,
														})
													}
													onKeyDown={(e) => {
														if (e.key === "Escape") {
															e.currentTarget.blur();
														}
													}}
													placeholder={t("group.enterPlaceholderText")}
													type="text"
													value={groupField.placeholder || ""}
												/>
												<p
													className="text-muted-foreground text-xs"
													id={`${groupField.id}-placeholder-help`}
												>
													{t("group.placeholderForThisField")}
												</p>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<div className="flex flex-col gap-1">
												<Label
													className="font-medium text-sm"
													htmlFor={`${groupField.id}-required`}
												>
													{t("group.requiredField")}
												</Label>
												<p className="text-muted-foreground text-xs">
													{t("group.makeFieldMandatory")}
												</p>
											</div>
											<Switch
												aria-describedby={`${groupField.id}-required-help`}
												checked={groupField.required}
												id={`${groupField.id}-required`}
												name={`${groupField.id}-required`}
												onCheckedChange={(checked) =>
													updateGroupField(groupField.id, { required: checked })
												}
											/>
										</div>

										<Collapsible
											onOpenChange={(open) =>
												setFieldExpansion(groupField.id, open)
											}
											open={!!expandedFields[groupField.id]}
										>
											<CollapsibleTrigger asChild>
												<Button
													aria-label={t("group.toggleAdvanced", { name: groupField.label || t(`types.${groupField.type}.label`) })}
													className="w-full justify-between"
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															setFieldExpansion(
																groupField.id,
																!expandedFields[groupField.id]
															);
														}
													}}
													type="button"
													variant="outline"
												>
													<span className="text-card-foreground text-sm">
														{t("group.advancedSettings")}
													</span>
													<ChevronDown
														aria-hidden="true"
														className={`size-4 transition-transform ${
															expandedFields[groupField.id] ? "rotate-180" : ""
														}`}
													/>
												</Button>
											</CollapsibleTrigger>
											<CollapsibleContent className="mt-2 flex flex-col gap-4">
												<div className="bg-background">
													<FieldSpecificSettings
														field={groupField}
														onFieldUpdate={(updatedField) =>
															updateGroupField(groupField.id, updatedField)
														}
														onUpdateSettings={(settings) =>
															updateGroupField(groupField.id, {
																settings: {
																	...groupField.settings,
																	...settings,
																},
															})
														}
													/>
												</div>
											</CollapsibleContent>
										</Collapsible>
									</div>
								</Card>
							))}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
